import * as GroupsSelectors from '../groups';
import * as CurrentsSelectors from '../current';
import * as LoadingSelectors from '../loading';
import * as FilesSelectors from '../files';
import * as SettingsSelectors from '../settings';
import * as Selectors from '..';

export const get = (state = {}) => state.comments || {};

export const getCommentById = (state = {}, id) => get(state)[id] || {};

export const isPendingById = (state = {}, id) => {
    const { pending } = getCommentById(state, id);

    return !!pending;
};

export const getCommentsIds = (state) => Object.keys(get(state));

export const getPendingIds = (state = {}) =>
    Object.keys(get(state)).filter((id) => isPendingById(state, id));

export const getPendingIdsLength = (state) => getPendingIds(state).length;

export const getCommentsByGroupId = (state, groupId) => {
    const comments = get(state);

    return Object.values(comments)
        .filter((comment) => comment.groupId === groupId)
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getCommentsByGroupType = (state, groupType) => {
    const groupId = GroupsSelectors.getGroupIdByType(groupType);
    return getCommentsByGroupId(state, groupId);
};

export const getCommentsByFileId = (state, fileId) => {
    const comments = get(state);
    return Object.values(comments)
        .filter((comment) => comment.fileId === fileId)
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getCurrentComments = (state) => {
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);
    return getCommentsByGroupId(state, currentGroupId);
};

export const getCommentsByFileIdAndGroupId = (state, groupId, fileId) => {
    const comments = get(state);
    return Object.values(comments)
        .filter((comment) =>
            comment.fileId === fileId && comment.groupId === groupId
        )
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);

};

export const getCommentsByFileIdAndCurrentGroup = (state, fileId) => {
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);

    const comments = get(state);
    return Object.values(comments)
        .filter((comment) =>
            comment.fileId === fileId && comment.groupId === currentGroupId
        )
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getPendingCommentsByFileIdAndGroupId = (state, groupId, fileId) => {
    const comments = get(state);
    return Object.values(comments)
        .filter((comment) =>
            comment.fileId === fileId && comment.groupId === groupId && comment.pending
        );
};

export const getCommentsByCurrentFileAndCurrentGroup = (state) => {
    const currentFileId = CurrentsSelectors.getCurrentFileId(state);
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);

    return getCommentsByFileIdAndGroupId(state, currentGroupId, currentFileId);
};

export const isReply = (state = {}, commentId) => {
    const comments = get(state);
    return !!comments[commentId].parentId;
};

export const getCommentsCountByCommentIdGroupedByFilerrId = (state = {}, commentIds) =>
    commentIds.reduce(
        (accumulator, id) => {
            const fileId = getFileIdByCommentId(state, id);
            const filerrId = FilesSelectors.getFilerrIdByFileId(state, fileId);
            const counter = accumulator[filerrId] || 0;

            accumulator[filerrId] = counter + 1;
            return accumulator;
        },
        {}
    );

export const getReplies = (state) => {
    const comments = get(state);
    return Object.values(comments).filter((comment) => isReply(state, comment.id))
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getCurrentReplies = (state) => {
    const comments = getCommentsByCurrentFileAndCurrentGroup(state);
    return comments.filter((comment) => isReply(state, comment.id));
};

export const getCommentsWithoutRepliesByCurrentFileId = (state) => {
    const comments = getCommentsByCurrentFileAndCurrentGroup(state);
    return comments.filter((comment) => !(isReply(state, comment.id)));
};

export const getCurrentCommentsWithoutRepliesByFileId = (state, fileId) => {
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);
    const comments = getCommentsByFileIdAndGroupId(state, currentGroupId, fileId);
    return comments.filter((comment) => !(isReply(state, comment.id)));
};

export const getPendingCommentsWithoutRepliesByCurrentFileId = (state) => {
    const comments = getCommentsWithoutRepliesByCurrentFileId(state);
    return comments.filter((comment) => isPendingById(state, comment.id));
};

export const getPendingCommentsByFileId = (state = {}, fileId) => {
    const comments = getCommentsByFileId(state, fileId);
    return comments.filter((comment) => isPendingById(state, comment.id));
};

export const getPendingCommentsLengthByFileId = (state = {}, fileId) => getPendingCommentsByFileId(state, fileId).length;

export const getPendingRepliesByCurrentFileId = (state) => {
    const replies = getCurrentReplies(state);
    return replies.filter((reply) => isPendingById(state, reply.id));
};

export const getRepliesByCommentId = (state, commentId) => {
    const comments = getReplies(state);
    return comments.filter((comment) => comment.parentId === commentId);
};

export const getRepliesIdsByCommentId = (state, commentId) => {
    const replies = getRepliesByCommentId(state, commentId);
    return replies.map((reply) => reply.id);
};

export const getCommentsIdsWithoutRepliesByCurrentFileId = (state) => {
    const currentComments = getCommentsWithoutRepliesByCurrentFileId(state);
    return currentComments.map((comment) => comment.id);
};

export const getCurrentCommentsIdsWithoutRepliesByFileId = (state, fileId) => {
    const currentComments = getCurrentCommentsWithoutRepliesByFileId(state, fileId);
    return currentComments.map((comment) => comment.id);
};

export const getRepliesLengthByCommentId = (state, commentId) =>
    getRepliesByCommentId(state, commentId).length;

export const hasRepliesByCommentId = (state, commentId) => !!getRepliesLengthByCommentId(state, commentId);

export const getCommentsIdsByGroupId = (state, groupId) => {
    const comments = get(state);
    return Object.values(comments).filter((comment) => comment.groupId === groupId)
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt)
        .map((comment) => comment.id);
};

export const getCommentIdsByCurrentGroupId = (state) => {
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);
    return getCommentsIdsByGroupId(state, currentGroupId);
};

export const getMarkers = (state) =>
    getCommentsWithoutRepliesByCurrentFileId(state)
        .filter((comment) =>
            comment.timecode !== undefined && comment.timecode !== null
        );

export const getPendingComments = (state = {}) =>
    Object.values(get(state)).filter((comment) => isPendingById(state, comment.id));

export const getFileIdByCommentId = (state = {}, commentId) => {
    const { fileId } = getCommentById(state, commentId);

    return fileId;
};

export const canCreateComments = (state = {}) =>
    !!getPendingIdsLength(state) && !LoadingSelectors.isCreateCommentsLoading(state);

export const isCommentSelected = (state = {}, commentId, commentParentId) => {
    const currentCommentId = CurrentsSelectors.getCurrentCommentId(state);
    if (currentCommentId === undefined) { return false; }

    return currentCommentId === commentId || currentCommentId === commentParentId;
};

export const getAnnotationsByCommentId = (state = {}, commentId) => {
    const { annotations } = getCommentById(state, commentId);

    if (!annotations) {
        return [];
    }

    const currentCommentId = CurrentsSelectors.getCurrentCommentId(state) === commentId;

    return annotations.map((annotation) =>
        ({ ...annotation, commentId, isCurrent: currentCommentId })
    );
};

export const getCurrentAnnotations = (state = {}) =>
    getCommentsByCurrentFileAndCurrentGroup(state).reduce(
        (acc, { id }) => acc.concat(getAnnotationsByCommentId(state, id)),
        []
    );

export const getTempAnnotations = (state = {}) => {
    const { tempAnnotations = [] } = state;

    return tempAnnotations.map((annotation) => ({
        ...annotation, isCurrent: true
    }));
};

export const shouldShowCommentsByFileId = (state = {}, fileId) => {
    if (!SettingsSelectors.isPostRevisionBehavior(state)) { return true; }
    if (GroupsSelectors.getCurrentGroupType(state) !== 'seller') { return true; }

    return !!getCommentsIdsWithoutRepliesByFileIdAndCurrentGroupIdWithoutCurrentReviewId(state, fileId).length;
};

export const getCommentsByCurrentReviewId = (state) => {
    const comments = get(state);
    const reviewId = CurrentsSelectors.getCurrentReviewId(state);

    return Object.values(comments)
        .filter((comment) => comment.reviewId === reviewId)
        .sort((replyA, replyB) => replyA.createdAt - replyB.createdAt);
};

export const getCommentsWithoutRepliesByCurrentReviewIdAndFileId = (state, fileId) => {
    const comments = get(state);
    const reviewId = CurrentsSelectors.getCurrentReviewId(state);

    return Object.values(comments)
        .filter((comment) => comment.reviewId === reviewId && !isReply(state, comment.id) && comment.fileId === fileId)
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getCommentIdsWithoutRepliesByFileIdByCurrentReviewId = (state, fileId) =>
    getCommentsWithoutRepliesByCurrentReviewIdAndFileId(state, fileId).map((comment) => comment.id);

export const getCommentsWithoutRepliesByFileIdWithoutCurrentReviewId = (state, fileId) => {
    const comments = get(state);
    const reviewId = CurrentsSelectors.getCurrentReviewId(state);

    return Object.values(comments)
        .filter((comment) => comment.reviewId !== reviewId && !isReply(state, comment.id) && comment.fileId === fileId)
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getCommentIdsWithoutRepliesByFileIdWithoutCurrentReviewId = (state, fileId) =>
    getCommentsWithoutRepliesByFileIdWithoutCurrentReviewId(state, fileId).map((comment) => comment.id);

export const getCommentsWithoutRepliesByFileIdAndGroupIdWithoutCurrentReviewId = (state, fileId, groupId) => {
    const comments = get(state);
    const reviewId = CurrentsSelectors.getCurrentReviewId(state);

    return Object.values(comments)
        .filter((comment) => comment.reviewId !== reviewId && !isReply(state, comment.id) && comment.fileId === fileId && comment.groupId === groupId)
        .sort((commentA, commentB) => commentA.createdAt - commentB.createdAt);
};

export const getCommentsIdsWithoutRepliesByFileIdAndGroupIdWithoutCurrentReviewId = (state, fileId, groupId) =>
    getCommentsWithoutRepliesByFileIdAndGroupIdWithoutCurrentReviewId(state, fileId, groupId).map((comment) => comment.id);

export const getCommentsIdsWithoutRepliesByFileIdAndCurrentGroupIdWithoutCurrentReviewId = (state, fileId) => {
    const groupId = CurrentsSelectors.getCurrentGroupId(state);
    return getCommentsIdsWithoutRepliesByFileIdAndGroupIdWithoutCurrentReviewId(state, fileId, groupId);
};

export const getCommentIdsWithoutRepliesByFileId = (state, fileId, withCurrentReview = false) => {
    if (Selectors.isSellerGroupInPostRevisionMode(state)) {
        if (withCurrentReview) {
            return getCommentIdsWithoutRepliesByFileIdByCurrentReviewId(state, fileId);
        } else {
            return getCommentIdsWithoutRepliesByFileIdWithoutCurrentReviewId(state, fileId);
        }
    } else {
        return getCurrentCommentsIdsWithoutRepliesByFileId(state, fileId);
    }
};

export const getReviewCreatedAt = (state) => {
    const commentsByReviewId = getCommentsByCurrentReviewId(state);

    if (commentsByReviewId.length) {
        return commentsByReviewId[0].createdAt;
    }

    return undefined;
};

export const getCommentsByCollaboratorId = (state, id) => {
    const allComments = getCurrentComments(state);
    return allComments.filter((comment) => getCollaboratorId(comment) === id);
};

const getCollaboratorId = (comment) => comment.collaboratorId;

export const getCurrentNonPendingComments = (state) => {
    const currentGroupId = CurrentsSelectors.getCurrentGroupId(state);
    return getCommentsByGroupId(state, currentGroupId).filter((comment) => !comment.pending);
};
